/**
 * KD Mobile Menu Slideshow
 * Handles the banner carousel with animated progress bars inside the mobile menu drawer.
 */
(function () {
  'use strict';

  class KdMobileSlideshow {
    constructor(el) {
      this.el = el;
      this.slides = [...el.querySelectorAll('.kd-mm-slide')];
      this.bars  = [...el.querySelectorAll('.kd-mm-progress-bar')];
      this.current = 0;
      this.duration = parseInt(el.querySelector('.kd-mm-slideshow__progress')?.dataset.duration, 10) || 5000;
      this.timer = null;
      this.running = false;

      if (this.slides.length < 2) return;

      this.bars.forEach((bar, i) => {
        bar.addEventListener('click', () => this.goTo(i));
      });

      this._initSwipe();

      const drawer = document.getElementById('m-menu-drawer');
      if (drawer) {
        const observer = new MutationObserver(() => {
          if (drawer.classList.contains('open')) {
            this.start();
          } else {
            this.pause();
          }
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

        if (drawer.classList.contains('open')) this.start();
      } else {
        this.start();
      }
    }

    /** Activate slide at given index, resetting the bar animation */
    goTo(index) {
      // Deactivate current
      this.slides[this.current].classList.remove('is-active');
      this.bars[this.current].classList.remove('is-active');

      // Mark all prior bars as completed
      this.bars.forEach((bar, i) => {
        if (i < index) {
          bar.classList.add('is-done');
          bar.classList.remove('is-active');
        } else if (i > index) {
          bar.classList.remove('is-done', 'is-active');
        }
      });

      this.current = index;
      this.slides[this.current].classList.add('is-active');
      this._activateBar(this.bars[this.current]);

      this._resetTimer();
    }

    /** Advance to the next slide */
    next() {
      const bar = this.bars[this.current];
      bar.classList.remove('is-active');
      bar.classList.add('is-done');
      this.slides[this.current].classList.remove('is-active');

      this.current = (this.current + 1) % this.slides.length;

      // If we wrapped around, reset all "done" states
      if (this.current === 0) {
        this.bars.forEach(b => b.classList.remove('is-done', 'is-active'));
      }

      this.slides[this.current].classList.add('is-active');
      this._activateBar(this.bars[this.current]);
    }

    _initSwipe() {
      const track = this.el.querySelector('.kd-mm-slideshow__track');
      if (!track) return;

      let startX = 0;

      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
      }, { passive: true });

      track.addEventListener('touchend', (e) => {
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) < 40) return; // ignore small swipes
        if (delta < 0) {
          // swipe left → next
          this.goTo((this.current + 1) % this.slides.length);
        } else {
          // swipe right → prev
          this.goTo((this.current - 1 + this.slides.length) % this.slides.length);
        }
      }, { passive: true });
    }

    start() {
      if (this.running) return;
      this.running = true;
      // Ensure the first bar is animated (handles reopening)
      this._activateBar(this.bars[this.current]);
      this.timer = setInterval(() => this.next(), this.duration);
    }

    pause() {
      this.running = false;
      clearInterval(this.timer);
      this.timer = null;
    }

    /** Restart timer after a manual goTo */
    _resetTimer() {
      this.pause();
      this.running = true;
      this.timer = setInterval(() => this.next(), this.duration);
    }

    /** Trigger CSS animation reset by removing and re-adding the class */
    _activateBar(bar) {
      const fill = bar.querySelector('.kd-mm-progress-bar__fill');
      if (!fill) return;

      bar.classList.remove('is-active');
      // Force reflow so the browser resets the animation
      void fill.offsetWidth;
      bar.classList.add('is-active');
    }
  }

  /**
   * Filter `.kd-mm-slide` elements by their `data-page-paths` targeting rule.
   * A slide with no rule always shows. A slide with a rule shows if the
   * current path matches one of its listed paths, OR if this visitor already
   * matched it earlier in the session (remembered in sessionStorage) — so the
   * banner keeps following them after they navigate off the matching page.
   * Non-matching slides (and their progress dot) are removed before the
   * carousel reads the DOM, keeping slides/dots aligned by index.
   */
  function filterSlidesByPage(slideshow) {
    const slides = [...slideshow.querySelectorAll('.kd-mm-slide')];
    const bars = [...slideshow.querySelectorAll('.kd-mm-progress-bar')];
    const path = window.location.pathname;

    slides.forEach(function (slide, i) {
      const key = slide.dataset.slideKey;
      const rawPaths = slide.dataset.pagePaths || '';
      const paths = rawPaths.split('\n').map(function (p) { return p.trim(); }).filter(Boolean);

      if (!paths.length) return; // no targeting rule, always show

      const storageKey = 'kd-banner-seen-' + key;
      const matchesNow = paths.some(function (p) { return path.indexOf(p) !== -1; });
      let visible = matchesNow;

      try {
        if (matchesNow) {
          sessionStorage.setItem(storageKey, '1');
        } else {
          visible = sessionStorage.getItem(storageKey) === '1';
        }
      } catch (e) {
        // sessionStorage unavailable (e.g. private mode) — fall back to current-page match only
      }

      if (!visible) {
        slide.remove();
        if (bars[i]) bars[i].remove();
      }
    });

    const remainingSlides = [...slideshow.querySelectorAll('.kd-mm-slide')];
    const remainingBars = [...slideshow.querySelectorAll('.kd-mm-progress-bar')];

    if (!remainingSlides.length) {
      slideshow.style.display = 'none';
      return;
    }

    remainingSlides.forEach(function (s, i) { s.classList.toggle('is-active', i === 0); });
    remainingBars.forEach(function (b, i) { b.classList.toggle('is-active', i === 0); });

    if (remainingBars.length < 2) {
      const progress = slideshow.querySelector('.kd-mm-slideshow__progress');
      if (progress) progress.style.display = 'none';
    }
  }

  /**
   * Fetch live review count from Trustpilot's Consumer API and update
   * the `.kd-mm-review-count` element inside the drawer.
   * Only runs once (result is cached on the element after the first open).
   */
  function fetchTrustpilotCount(container) {
    const reviewEl = container.querySelector('.kd-mm-review');
    if (!reviewEl) return;

    const apiKey  = reviewEl.dataset.tpApikey;
    const buId    = reviewEl.dataset.tpBusinessunit;
    const countEl = reviewEl.querySelector('.kd-mm-review-count');

    if (!apiKey || !buId || !countEl) return;
    if (reviewEl.dataset.tpLoaded) return; // already fetched
    reviewEl.dataset.tpLoaded = '1';

    fetch(`https://api.trustpilot.com/v1/business-units/${buId}?apikey=${encodeURIComponent(apiKey)}`)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.numberOfReviews) return;
        var total = data.numberOfReviews.total;
        // Format as e.g. "7.782+" matching the Dutch locale style used on the site
        countEl.textContent = total.toLocaleString('nl-NL') + '+';
      })
      .catch(function () { /* keep static fallback on any error */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const slideshow = document.querySelector('.kd-mm-slideshow');
    if (slideshow) {
      filterSlidesByPage(slideshow);
      if (slideshow.style.display !== 'none') {
        new KdMobileSlideshow(slideshow);
      }
    }

    const drawer = document.getElementById('m-menu-drawer');
    if (drawer) {
      const observer = new MutationObserver(() => {
        if (drawer.classList.contains('open')) fetchTrustpilotCount(drawer);
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      if (drawer.classList.contains('open')) fetchTrustpilotCount(drawer);
    }
  }
})();
